import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { CheckIcon, ChevronDownIcon } from "@heroicons/react/24/outline";

const SIZE_CLS = {
  sm: "px-2.5 py-2 text-[0.875rem] rounded-lg",
  md: "px-3 py-2.5 text-[1rem] rounded-lg",
  lg: "px-3 py-3 text-[1rem] rounded-lg",
};

function optionsFromChildren(children) {
  const out = [];
  React.Children.forEach(children, (child) => {
    if (!child || child.type !== "option") return;
    out.push({
      value: child.props.value == null ? "" : String(child.props.value),
      label: String(child.props.children ?? ""),
      disabled: Boolean(child.props.disabled),
    });
  });
  return out;
}

/**
 * Select personalizado (reemplazo de <select> nativo).
 * onChange compatible: recibe evento sintético { target: { value } }.
 */
export default function PortalSelect({
  value = "",
  onChange,
  options: optionsProp,
  children,
  disabled = false,
  className = "",
  size = "md",
  placeholder,
  "aria-label": ariaLabel,
  id,
  name,
}) {
  const listId = useId();
  const rootRef = useRef(null);
  const buttonRef = useRef(null);
  const listRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState(null);
  const [highlight, setHighlight] = useState(-1);

  const options = useMemo(() => {
    if (Array.isArray(optionsProp) && optionsProp.length) return optionsProp;
    return optionsFromChildren(children);
  }, [optionsProp, children]);

  const selected = options.find((o) => String(o.value) === String(value));
  const displayLabel =
    selected?.label ||
    placeholder ||
    options.find((o) => o.value === "")?.label ||
    "Seleccionar";

  function emit(next) {
    if (disabled) return;
    onChange?.({
      target: { value: next, name: name || undefined },
      currentTarget: { value: next, name: name || undefined },
    });
  }

  function updatePosition() {
    const el = buttonRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const maxH = 280;
    const spaceBelow = window.innerHeight - r.bottom;
    const openUp = spaceBelow < Math.min(maxH, 160) && r.top > spaceBelow;
    setMenuPos({
      left: r.left,
      width: Math.max(r.width, 140),
      top: openUp ? undefined : r.bottom + 4,
      bottom: openUp ? window.innerHeight - r.top + 4 : undefined,
      maxHeight: openUp
        ? Math.min(maxH, r.top - 12)
        : Math.min(maxH, spaceBelow - 12),
    });
  }

  useEffect(() => {
    if (!open) return undefined;
    updatePosition();
    const onScroll = () => updatePosition();
    const onResize = () => updatePosition();
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onResize);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const idx = Math.max(
      0,
      options.findIndex((o) => String(o.value) === String(value) && !o.disabled)
    );
    setHighlight(idx === -1 ? 0 : idx);

    function onDoc(e) {
      if (rootRef.current?.contains(e.target)) return;
      if (listRef.current?.contains(e.target)) return;
      setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open, options, value]);

  useEffect(() => {
    if (!open || highlight < 0) return;
    const node = listRef.current?.querySelector(`[data-opt-index="${highlight}"]`);
    node?.scrollIntoView({ block: "nearest" });
  }, [open, highlight]);

  function moveHighlight(delta) {
    if (!options.length) return;
    let i = highlight;
    for (let n = 0; n < options.length; n += 1) {
      i = (i + delta + options.length) % options.length;
      if (!options[i].disabled) {
        setHighlight(i);
        return;
      }
    }
  }

  function pick(opt) {
    if (!opt || opt.disabled) return;
    emit(String(opt.value));
    setOpen(false);
    buttonRef.current?.focus();
  }

  function onKeyDown(e) {
    if (disabled) return;
    if (!open && (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ")) {
      e.preventDefault();
      setOpen(true);
      return;
    }
    if (!open) return;
    if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      moveHighlight(1);
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      moveHighlight(-1);
      return;
    }
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      pick(options[highlight]);
    }
  }

  const sizeCls = SIZE_CLS[size] || SIZE_CLS.md;

  const menu =
    open && menuPos
      ? createPortal(
          <ul
            ref={listRef}
            id={listId}
            role="listbox"
            data-portal-select-menu
            className="fixed z-[120] overflow-auto rounded-xl border border-brand-lavender/25 bg-white py-1 shadow-lg shadow-brand-navy/10"
            style={{
              left: menuPos.left,
              width: menuPos.width,
              top: menuPos.top,
              bottom: menuPos.bottom,
              maxHeight: menuPos.maxHeight,
            }}
          >
            {options.map((opt, i) => {
              const isSelected = String(opt.value) === String(value);
              const isHi = i === highlight;
              return (
                <li
                  key={`${opt.value}-${i}`}
                  data-opt-index={i}
                  role="option"
                  aria-selected={isSelected}
                  aria-disabled={opt.disabled || undefined}
                  className={[
                    "flex cursor-pointer items-center gap-2 px-3 py-2.5 text-[1rem] transition-colors",
                    opt.disabled
                      ? "cursor-not-allowed opacity-40"
                      : isHi
                        ? "bg-brand-lilac/50 text-brand-navy"
                        : "text-brand-dark hover:bg-brand-lilac/30",
                    isSelected ? "font-semibold" : "font-normal",
                  ].join(" ")}
                  onMouseEnter={() => !opt.disabled && setHighlight(i)}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => pick(opt)}
                >
                  <span className="w-4 shrink-0 flex justify-center">
                    {isSelected ? (
                      <CheckIcon className="h-4 w-4 text-brand-lavender" aria-hidden />
                    ) : null}
                  </span>
                  <span className="min-w-0 flex-1 truncate">{opt.label}</span>
                </li>
              );
            })}
          </ul>,
          document.body
        )
      : null;

  return (
    <div ref={rootRef} className={`relative block min-w-0 w-full ${className}`.trim()}>
      <button
        ref={buttonRef}
        type="button"
        id={id}
        disabled={disabled}
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        onClick={() => !disabled && setOpen((v) => !v)}
        onKeyDown={onKeyDown}
        className={[
          "w-full inline-flex items-center justify-between gap-2 border bg-white text-left text-brand-dark",
          "border-blue-gray-200 hover:border-brand-lavender/50",
          "focus:border-brand-lavender focus:outline-none focus:ring-1 focus:ring-brand-lavender/40",
          "disabled:opacity-50 disabled:cursor-not-allowed",
          sizeCls,
        ].join(" ")}
      >
        <span className={`min-w-0 truncate ${selected ? "" : "text-brand-gray"}`}>
          {displayLabel}
        </span>
        <ChevronDownIcon
          className={`h-4 w-4 shrink-0 text-brand-gray transition-transform ${
            open ? "rotate-180" : ""
          }`}
          aria-hidden
        />
      </button>
      {menu}
    </div>
  );
}
